// /admin/audit — searchable audit log page (T085).
//
// Layout:
//   - Header: eyebrow + title, link to /admin/users.
//   - Filter bar: free-text search (q), action dropdown, actor email input.
//     Filter state lives in the URL so deep links and refresh preserve context.
//   - Table: time, actor, action, target, status code (from metadata).
//   - Load-more button: cursor-based pagination via next_cursor.
//
// Only org_admin users can reach this route; the backend RequireRole middleware
// enforces it. The frontend does not gate the route itself.

import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  ClipboardList,
  Download,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import {
  ADMIN_LICENSE_KEY,
  ApiError,
  AuditEvent,
  AuditListParams,
  LicenseStatus,
  adminAuditApi,
  downloadReport,
  fetcher,
} from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminAudit() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state from URL.
  const q = searchParams.get('q') ?? '';
  const action = searchParams.get('action') ?? '';
  const actor = searchParams.get('actor') ?? '';

  // Local draft state — committed on form submit so every keystroke doesn't
  // trigger a new fetch.
  const [draftQ, setDraftQ] = useState(q);
  const [draftAction, setDraftAction] = useState(action);
  const [draftActor, setDraftActor] = useState(actor);

  // Reset drafts when URL changes externally (e.g. browser back).
  useEffect(() => {
    setDraftQ(q);
    setDraftAction(action);
    setDraftActor(actor);
  }, [q, action, actor]);

  const applyFilters = useCallback((e: FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (draftQ.trim()) p.set('q', draftQ.trim());
    if (draftAction.trim()) p.set('action', draftAction.trim());
    if (draftActor.trim()) p.set('actor', draftActor.trim());
    setSearchParams(p, { replace: true });
  }, [draftQ, draftAction, draftActor, setSearchParams]);

  const clearFilters = () => {
    setDraftQ(''); setDraftAction(''); setDraftActor('');
    setSearchParams({}, { replace: true });
  };

  const hasFilters = q || action || actor;

  return (
    <div className="max-w-6xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        title="Audit Log"
        description={
          <>
            All mutating API calls by org members. Read-only; rows are appended
            by the server and can never be deleted.
          </>
        }
        actions={
          <>
            <SiemExportButton />
            <Link
              to="/admin/users"
              className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
            >
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              User Management
            </Link>
          </>
        }
      />

      {/* Filter bar */}
      <form
        onSubmit={applyFilters}
        className="orb-card p-4 mb-4 flex flex-wrap gap-3 items-end"
        role="search"
        aria-label="Audit log filters"
      >
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <label className="orb-section-title text-[11px]" htmlFor="audit-q">
            Search
          </label>
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none"
              aria-hidden="true"
            />
            <input
              id="audit-q"
              type="search"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder="action, target, or actor…"
              className="
                w-full pl-8 pr-3 py-1.5 rounded-md border border-border
                bg-bg-inset text-fg text-xs placeholder-fg-subtle
                focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
                transition-colors
              "
            />
          </div>
        </div>

        <div className="flex flex-col gap-1 min-w-[160px]">
          <label className="orb-section-title text-[11px]" htmlFor="audit-action">
            Action
          </label>
          <input
            id="audit-action"
            type="text"
            value={draftAction}
            onChange={(e) => setDraftAction(e.target.value)}
            placeholder="e.g. projects.create"
            className="
              px-3 py-1.5 rounded-md border border-border
              bg-bg-inset text-fg text-xs placeholder-fg-subtle
              focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
              transition-colors
            "
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[180px]">
          <label className="orb-section-title text-[11px]" htmlFor="audit-actor">
            Actor email
          </label>
          <input
            id="audit-actor"
            type="text"
            value={draftActor}
            onChange={(e) => setDraftActor(e.target.value)}
            placeholder="user@example.com"
            className="
              px-3 py-1.5 rounded-md border border-border
              bg-bg-inset text-fg text-xs placeholder-fg-subtle
              focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
              transition-colors
            "
          />
        </div>

        <div className="flex gap-2 items-end">
          <button
            type="submit"
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              bg-accent text-accent-fg hover:opacity-90
              border border-accent/40 transition-opacity
            "
          >
            <Search className="h-3 w-3" aria-hidden="true" />
            Filter
          </button>
          {hasFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className="
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
                text-xs font-semibold uppercase tracking-[0.08em]
                text-fg-muted hover:text-fg hover:bg-bg-hover
                border border-border hover:border-border-strong
                transition-colors
              "
            >
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              Clear
            </button>
          ) : null}
        </div>
      </form>

      <AuditTable params={{ q: q || undefined, action: action || undefined, actor: actor || undefined, limit: PAGE_SIZE }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SIEM export (T339) — downloads GET /api/v1/admin/audit/siem-export
// (CEF-format, oldest-first). Gated behind the `siem_export` license
// feature: hidden while the license status is still loading, an
// upgrade-prompt link to /admin/license when the org's license doesn't
// grant the feature, a working download button when it does.
// ---------------------------------------------------------------------------

function SiemExportButton() {
  const { data: license } = useSWR<LicenseStatus>(ADMIN_LICENSE_KEY, fetcher, {
    revalidateOnFocus: false,
  });
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!license) return null;

  const enabled = license.valid && (license.features ?? []).includes('siem_export');

  if (!enabled) {
    return (
      <Link
        to="/admin/license"
        title="Requires the SIEM Export license feature — upgrade to enable"
        className="inline-flex items-center gap-1.5 text-xs text-fg-subtle hover:text-fg transition-colors"
      >
        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
        Export for SIEM
      </Link>
    );
  }

  const handleExport = async () => {
    if (downloading) return;
    setDownloading(true);
    setError(null);
    const today = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    try {
      await downloadReport(adminAuditApi.siemExportURL(), `audit-siem-export-${today}.log`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`${err.code}: ${err.message}`);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Export failed');
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {error ? <span className="text-xs text-danger">{error}</span> : null}
      <button
        type="button"
        onClick={handleExport}
        disabled={downloading}
        className="
          inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg
          transition-colors disabled:opacity-50 disabled:cursor-not-allowed
        "
      >
        {downloading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        Export for SIEM
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit table with load-more
// ---------------------------------------------------------------------------

interface AuditTableProps {
  readonly params: AuditListParams;
}

function AuditTable({ params }: AuditTableProps) {
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [initialised, setInitialised] = useState(false);

  // When filters change, reset and re-fetch from the top.
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setCursor('');
    setHasMore(false);
    setError(null);
    setInitialised(false);
    setLoading(true);

    adminAuditApi.search(params).then((res) => {
      if (cancelled) return;
      setRows(res.items);
      setCursor(res.next_cursor);
      setHasMore(res.next_cursor !== '');
      setInitialised(true);
    }).catch((err) => {
      if (cancelled) return;
      setError(err);
      setInitialised(true);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const loadMore = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const res = await adminAuditApi.search({ ...params, before_id: cursor });
      setRows((prev) => [...prev, ...res.items]);
      setCursor(res.next_cursor);
      setHasMore(res.next_cursor !== '');
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!initialised) {
    return (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading audit log…</span>
      </div>
    );
  }

  if (error) return <ErrorBanner error={error} />;
  if (rows.length === 0) return <EmptyState />;

  return (
    <div className="space-y-3">
      <Table dense>
        <THead>
          <TR>
            <TH className="whitespace-nowrap">Time</TH>
            <TH>Actor</TH>
            <TH>Action</TH>
            <TH>Target</TH>
            <TH>Status</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((ev) => (
            <AuditRow key={ev.id} event={ev} />
          ))}
        </TBody>
      </Table>

      {hasMore ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            className="
              inline-flex items-center gap-1.5 px-4 py-2 rounded-md
              text-xs font-semibold uppercase tracking-[0.08em]
              text-fg-muted hover:text-fg hover:bg-bg-hover
              border border-border hover:border-border-strong
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-colors
            "
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : (
        <p className="text-center text-xs text-fg-subtle py-2">
          End of audit log
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single row
// ---------------------------------------------------------------------------

function AuditRow({ event }: { readonly event: AuditEvent }) {
  const status = event.metadata?.http_status as number | undefined;
  return (
    <TR className="font-mono text-xs">
      <TD mono className="whitespace-nowrap">
        {formatTime(event.created_at)}
      </TD>
      <TD primary mono className="truncate max-w-[180px]">
        {event.actor_email || (
          <span className="text-fg-subtle italic">project-token</span>
        )}
      </TD>
      <TD>
        <span className="bg-accent/10 text-accent px-1.5 py-0.5 rounded text-[11px] font-semibold">
          {event.action}
        </span>
      </TD>
      <TD mono className="truncate max-w-[240px]" title={event.target}>
        {event.target || '—'}
      </TD>
      <TD>
        {status === undefined ? (
          <span className="text-fg-subtle">—</span>
        ) : (
          <StatusChip status={status} />
        )}
      </TD>
    </TR>
  );
}

function StatusChip({ status }: { readonly status: number }) {
  const isOK = status >= 200 && status < 300;
  return (
    <span
      className={[
        'inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold',
        isOK ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning',
      ].join(' ')}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function EmptyState() {
  return (
    <div className="orb-card p-10 text-center">
      <p className="orb-eyebrow mb-2">no events</p>
      <div className="flex justify-center mb-3">
        <ClipboardList className="h-8 w-8 text-fg-subtle" aria-hidden="true" />
      </div>
      <h2 className="font-display text-xl font-semibold text-fg mb-2">
        Audit log is empty.
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">
        Mutating API calls (POST, PATCH, DELETE) will appear here once made.
        Try clearing any active filters.
      </p>
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can view the audit log.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}
